import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import api from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { DataTable, FinanceCard, NativeSelect, money, todayIso } from "./financeUi";

const blankLine = () => ({ item_id: "", name: "", unit: "", qty: 1, rate: "", tax_pct: 0, income_account: "" });

export default function FinanceSales({ accounts, parties, items, invoices, reload }) {
  const incomeAccounts = accounts.filter((a) => a.type === "income");
  const [header, setHeader] = useState({ date: todayIso(), due_date: "", party_id: "", party_name: "", description: "", income_account: "Other Income" });
  const [lines, setLines] = useState([blankLine()]);

  const setLine = (index, patch) => setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const pickItem = (index, itemId) => {
    const item = items.find((i) => i.item_id === itemId);
    setLine(index, item ? { item_id: item.item_id, name: item.name, unit: item.unit, rate: item.rate, tax_pct: item.tax_pct, income_account: item.income_account } : { item_id: "", name: "" });
  };
  const taxable = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.rate || 0), 0);
  const tax = lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.rate || 0) * Number(l.tax_pct || 0)) / 100, 0);

  const post = async () => {
    const party = header.party_id ? { party_id: header.party_id } : header.party_name ? { name: header.party_name, type: "OTHER" } : {};
    if (!party.party_id && !party.name) return toast.error("Select or type a party");
    const payload = { date: header.date, due_date: header.due_date, description: header.description, income_account: header.income_account, party, items: lines.filter((l) => Number(l.rate) > 0) };
    if (!payload.items.length) return toast.error("Add at least one line with a rate");
    try {
      await api.post("/admin/company-finance/invoices", payload);
      toast.success("Company invoice posted");
      setLines([blankLine()]);
      setHeader({ ...header, description: "", party_name: "" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post invoice");
    }
  };

  return (
    <div className="space-y-5">
      <FinanceCard testid="sales-invoice-form" title="Post sales invoice">
        <div className="grid gap-3 md:grid-cols-4">
          <Input data-testid="company-invoice-date-input" onChange={(e) => setHeader({ ...header, date: e.target.value })} type="date" value={header.date} />
          <Input data-testid="company-invoice-due-input" onChange={(e) => setHeader({ ...header, due_date: e.target.value })} placeholder="Due date" type="date" value={header.due_date} />
          <NativeSelect data-testid="company-invoice-party-select" onChange={(e) => setHeader({ ...header, party_id: e.target.value })} value={header.party_id}>
            <option value="">Party from master…</option>
            {parties.map((p) => <option key={p.party_id} value={p.party_id}>{p.name} ({p.type})</option>)}
          </NativeSelect>
          <Input data-testid="company-invoice-party-input" disabled={!!header.party_id} onChange={(e) => setHeader({ ...header, party_name: e.target.value })} placeholder="…or type ad-hoc party" value={header.party_name} />
        </div>
        <div className="space-y-2" data-testid="invoice-lines">
          {lines.map((line, index) => (
            <div className="grid gap-2 md:grid-cols-[1.4fr_1.4fr_.6fr_.8fr_.6fr_.9fr_auto] items-center" data-testid={`invoice-line-${index}`} key={index}>
              <NativeSelect data-testid={`invoice-line-item-${index}`} onChange={(e) => pickItem(index, e.target.value)} value={line.item_id}>
                <option value="">Item / service…</option>
                {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name}</option>)}
              </NativeSelect>
              <Input data-testid={`invoice-line-name-${index}`} onChange={(e) => setLine(index, { name: e.target.value })} placeholder="Description" value={line.name} />
              <Input data-testid={`invoice-line-qty-${index}`} onChange={(e) => setLine(index, { qty: e.target.value })} placeholder="Qty" type="number" value={line.qty} />
              <Input data-testid={`invoice-line-rate-${index}`} onChange={(e) => setLine(index, { rate: e.target.value })} placeholder="Rate" type="number" value={line.rate} />
              <Input data-testid={`invoice-line-tax-${index}`} onChange={(e) => setLine(index, { tax_pct: e.target.value })} placeholder="Tax %" type="number" value={line.tax_pct} />
              <div className="text-right text-sm tabular-nums" data-testid={`invoice-line-amount-${index}`}>{money(Number(line.qty || 0) * Number(line.rate || 0))}</div>
              <Button data-testid={`invoice-line-remove-${index}`} disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, i) => i !== index))} size="icon" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button data-testid="invoice-line-add" onClick={() => setLines([...lines, blankLine()])} size="sm" variant="outline"><Plus className="w-4 h-4" />Add line</Button>
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] items-end">
          <Input data-testid="company-invoice-description-input" onChange={(e) => setHeader({ ...header, description: e.target.value })} placeholder="Narration" value={header.description} />
          <NativeSelect data-testid="company-invoice-income-select" onChange={(e) => setHeader({ ...header, income_account: e.target.value })} value={header.income_account}>
            {incomeAccounts.map((a) => <option key={a.account_id} value={a.name}>{a.code} · {a.name}</option>)}
          </NativeSelect>
          <div className="text-right text-sm" data-testid="invoice-totals">
            <div>Taxable <strong>{money(taxable)}</strong> · Tax <strong>{money(tax)}</strong></div>
            <div className="font-display text-lg font-bold">Total {money(taxable + tax)}</div>
          </div>
        </div>
        <Button data-testid="company-invoice-save-button" onClick={post}><Plus className="w-4 h-4" />Post Invoice</Button>
      </FinanceCard>
      <DataTable
        columns={[
          ["Invoice", (i) => <span className="font-mono text-xs">{i.invoice_number}</span>],
          ["Date", (i) => i.date],
          ["Party", (i) => i.party?.name || i.party?.society_id || "—"],
          ["Total", (i) => money(i.total), "right"],
          ["Outstanding", (i) => money(i.outstanding), "right"],
          ["Status", (i) => <span className="text-xs rounded bg-muted px-2 py-0.5">{i.status}</span>],
          ["ID", (i) => <span className="font-mono text-[11px] text-muted-foreground">{i.invoice_id}</span>],
        ]}
        rowKey={(i) => i.invoice_id}
        rows={invoices}
        testid="invoice-table"
      />
    </div>
  );
}
